import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, Pencil, X, Trash2, KeyRound, Copy, Check, FileText, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { ImagePreviewDialog } from "@/components/ImagePreviewDialog";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError, fileUrl, openFileBlob, downloadFileBlob } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["ACTIVE", "PENDING", "EXPIRED", "SUSPENDED", "INACTIVE", "REVOKED"];
const DAERAH_LIST = ["Kuching", "Samarahan", "Sri Aman", "Sibu", "Bintulu", "Miri", "Lain-lain"];
const EDUCATION_LIST = ["UPSR", "PMR/PT3", "SPM", "STPM", "Sijil", "Diploma", "Ijazah Sarjana Muda", "Sarjana", "Lain-lain"];

const PROFILE_FIELDS = [
  { key: "full_name", label: "Nama Penuh" },
  { key: "identification_number", label: "No. Kad Pengenalan" },
  { key: "email", label: "Emel" },
  { key: "phone", label: "No. Telefon" },
  { key: "address", label: "Alamat" },
  { key: "company_name", label: "Syarikat" },
  { key: "position", label: "Jawatan" },
  { key: "occupation", label: "Pekerjaan" },
  { key: "experience_years", label: "Tahun Pengalaman" },
  { key: "course_name", label: "Nama Kursus" },
  { key: "course_date", label: "Tarikh Kursus" },
  { key: "course_session_year", label: "Sesi/Tahun Kursus" },
  { key: "course_venue", label: "Tempat Kursus" },
  { key: "next_of_kin_name", label: "Nama Waris" },
  { key: "next_of_kin_relationship", label: "Hubungan Waris" },
  { key: "next_of_kin_phone", label: "No. Telefon Waris" },
];

export default function AhliDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [member, setMember] = useState(null);
  const [categories, setCategories] = useState([]);
  const [newStatus, setNewStatus] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [newCategoryId, setNewCategoryId] = useState("");
  const [savingCategory, setSavingCategory] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [editingNumber, setEditingNumber] = useState(false);
  const [numberForm, setNumberForm] = useState("");
  const [savingNumber, setSavingNumber] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [validFrom, setValidFrom] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [validityReason, setValidityReason] = useState("");
  const [savingValidity, setSavingValidity] = useState(false);

  const load = () => api.get(`/members/${id}`).then((r) => {
    setMember(r.data);
    setNewStatus(r.data.status);
    setNewCategoryId(r.data.membership_category_id);
    setProfileForm(r.data.profile || {});
    setValidFrom(r.data.valid_from ? r.data.valid_from.slice(0, 10) : "");
    setValidUntil(r.data.valid_until ? r.data.valid_until.slice(0, 10) : "");
  }).catch(() => {});
  useEffect(() => { load(); api.get("/categories").then((r) => setCategories(r.data)).catch(() => {}); }, [id]);

  const canManage = ["super_admin", "secretariat", "president"].includes(user?.role);
  const canEditNumber = ["super_admin", "president"].includes(user?.role);

  const saveMemberNumber = async () => {
    setSavingNumber(true);
    try {
      await api.patch(`/members/${id}/member-number`, { member_number: numberForm });
      toast.success("No. Ahli dikemas kini");
      setEditingNumber(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingNumber(false);
    }
  };

  const changeStatus = async () => {
    setSaving(true);
    try {
      await api.patch(`/members/${id}/status`, { new_status: newStatus, reason });
      toast.success("Status ahli dikemas kini");
      setReason("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const changeCategory = async () => {
    setSavingCategory(true);
    try {
      await api.patch(`/members/${id}/category`, { category_id: newCategoryId });
      toast.success("Kategori keahlian dikemas kini");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingCategory(false);
    }
  };

  const changeValidity = async () => {
    if (!validityReason.trim()) {
      toast.error("Sebab perubahan tempoh keahlian diperlukan");
      return;
    }
    setSavingValidity(true);
    try {
      await api.patch(`/members/${id}/validity`, {
        valid_from: validFrom || null,
        valid_until: validUntil || null,
        reason: validityReason,
      });
      toast.success("Tempoh keahlian dikemas kini");
      setValidityReason("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingValidity(false);
    }
  };

  const renewOneYear = () => {
    const base = validUntil ? new Date(validUntil) : new Date();
    base.setFullYear(base.getFullYear() + 1);
    setValidUntil(base.toISOString().slice(0, 10));
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.put(`/members/${id}/profile`, profileForm);
      toast.success("Profil ahli dikemas kini");
      setEditingProfile(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingProfile(false);
    }
  };

  const deleteMember = async () => {
    setDeleting(true);
    try {
      await api.patch(`/members/${id}/status`, { new_status: "REVOKED", reason: "Dipadam oleh admin" });
      toast.success("Ahli dinyahaktifkan");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const resetPassword = async () => {
    if (!member.user_id) {
      toast.error("Ahli ini belum mempunyai akaun log masuk (belum tuntut keahlian)");
      return;
    }
    setResetting(true);
    try {
      const res = await api.post(`/admin/users/${member.user_id}/reset-password`);
      setResetResult(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setResetting(false);
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(resetResult.new_password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      await downloadFileBlob(`/members/${id}/resume-pdf`, `${member.member_number || "profil"}.pdf`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (!member) return <div className="text-piks-muted">Memuatkan...</div>;

  return (
    <div className="max-w-3xl" data-testid="ahli-detail-page">
      <Link to="/admin/ahli" className="inline-flex items-center gap-1.5 text-sm text-piks-muted hover:text-piks-black mb-6"><ArrowLeft className="w-4 h-4" /> Kembali</Link>

      <div className="flex items-center gap-4 mb-6 flex-wrap">
        <div className="w-14 h-14 rounded-full bg-piks-ivory border border-piks-border overflow-hidden flex items-center justify-center shrink-0">
          {(member.profile?.passport_photo_file_id || member.profile_photo_file_id) ? <img src={fileUrl(member.profile?.passport_photo_file_id || member.profile_photo_file_id)} alt="" className="w-full h-full object-cover" /> : <span className="font-display text-xl">{member.profile?.full_name?.[0] || "A"}</span>}
        </div>
        <div className="min-w-0">
          <h1 className="font-display text-xl font-bold text-piks-black truncate">{member.profile?.full_name}</h1>
          {editingNumber ? (
            <div className="flex items-center gap-2 mt-1">
              <Input
                data-testid="ahli-member-number-input"
                value={numberForm}
                onChange={(e) => setNumberForm(e.target.value)}
                className="h-8 w-40 font-mono text-sm"
              />
              <Button size="sm" className="h-8 bg-piks-black hover:bg-piks-black/90" onClick={saveMemberNumber} disabled={savingNumber} data-testid="ahli-member-number-save-btn">
                {savingNumber ? "..." : "Simpan"}
              </Button>
              <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditingNumber(false)} data-testid="ahli-member-number-cancel-btn"><X className="w-3.5 h-3.5" /></Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <p className="font-mono text-sm text-piks-muted">{member.member_number}</p>
              {canEditNumber && (
                <button onClick={() => { setNumberForm(member.member_number); setEditingNumber(true); }} data-testid="ahli-member-number-edit-btn" className="text-piks-muted hover:text-piks-black">
                  <Pencil className="w-3 h-3" />
                </button>
              )}
            </div>
          )}
        </div>
        <StatusBadge status={member.effective_status || member.status} className="ml-auto" />
      </div>

      {canManage && (
        <div className="flex items-center justify-end gap-2 mb-4 flex-wrap">
          <Button size="sm" variant="outline" onClick={downloadPdf} disabled={downloadingPdf} data-testid="ahli-download-pdf-btn">
            <FileDown className="w-3.5 h-3.5 mr-1.5" /> {downloadingPdf ? "Menjana..." : "Muat Turun PDF"}
          </Button>
          {!editingProfile && (
            <Button size="sm" variant="outline" onClick={() => setEditingProfile(true)} data-testid="ahli-edit-profile-btn">
              <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit Profil
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={resetPassword} disabled={resetting} data-testid="ahli-reset-password-btn">
            <KeyRound className="w-3.5 h-3.5 mr-1.5" /> {resetting ? "Menetapkan..." : "Reset Kata Laluan"}
          </Button>
          {member.status !== "REVOKED" && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="outline" className="border-piks-red text-piks-red hover:bg-piks-red hover:text-white" data-testid="ahli-delete-btn">
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Padam Ahli
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="ahli-delete-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Padam ahli "{member.profile?.full_name}"?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Ahli akan dinyahaktifkan (status ditukar ke REVOKED). Rekod dan sejarah keahlian dikekalkan untuk tujuan audit. Tindakan ini boleh diterbalikkan melalui Kemaskini Status.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Batal</AlertDialogCancel>
                  <AlertDialogAction onClick={deleteMember} disabled={deleting} data-testid="ahli-delete-confirm-btn" className="bg-piks-red hover:bg-piks-red/90">
                    {deleting ? "Memadam..." : "Padam Ahli"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      )}

      {editingProfile ? (
        <div className="bg-white rounded-xl border border-piks-border p-6 mb-6" data-testid="ahli-edit-profile-form">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-piks-muted uppercase tracking-wide">Edit Profil Ahli</h2>
            <Button size="sm" variant="ghost" onClick={() => { setEditingProfile(false); setProfileForm(member.profile || {}); }} data-testid="ahli-edit-profile-cancel-btn"><X className="w-4 h-4" /></Button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {PROFILE_FIELDS.map((f) => (
              <div key={f.key}>
                <Label>{f.label}</Label>
                <Input
                  data-testid={`edit-profile-field-${f.key}`}
                  type={f.key === "experience_years" ? "number" : "text"}
                  value={profileForm[f.key] ?? ""}
                  onChange={(e) => setProfileForm((cur) => ({ ...cur, [f.key]: f.key === "experience_years" ? parseInt(e.target.value) || 0 : e.target.value }))}
                  className="mt-1.5"
                />
              </div>
            ))}
            <div>
              <Label>Jantina</Label>
              <Select value={profileForm.gender || ""} onValueChange={(v) => setProfileForm((cur) => ({ ...cur, gender: v }))}>
                <SelectTrigger data-testid="edit-profile-field-gender" className="mt-1.5"><SelectValue placeholder="Pilih jantina" /></SelectTrigger>
                <SelectContent><SelectItem value="Lelaki">Lelaki</SelectItem><SelectItem value="Perempuan">Perempuan</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label>Daerah</Label>
              <Select value={profileForm.daerah || ""} onValueChange={(v) => setProfileForm((cur) => ({ ...cur, daerah: v }))}>
                <SelectTrigger data-testid="edit-profile-field-daerah" className="mt-1.5"><SelectValue placeholder="Pilih daerah" /></SelectTrigger>
                <SelectContent>{DAERAH_LIST.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tahap Pendidikan</Label>
              <Select value={profileForm.education_level || ""} onValueChange={(v) => setProfileForm((cur) => ({ ...cur, education_level: v }))}>
                <SelectTrigger data-testid="edit-profile-field-education_level" className="mt-1.5"><SelectValue placeholder="Pilih tahap pendidikan" /></SelectTrigger>
                <SelectContent>{EDUCATION_LIST.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={saveProfile} disabled={savingProfile} data-testid="ahli-edit-profile-save-btn" className="mt-5 bg-piks-black hover:bg-piks-black/90">
            {savingProfile ? "Menyimpan..." : "Simpan Perubahan"}
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-piks-border p-6 mb-6 grid sm:grid-cols-2 gap-4 text-sm">
          <Info label="No. Kad Pengenalan" value={member.profile?.identification_number} />
          <Info label="Emel" value={member.profile?.email} />
          <Info label="No. Telefon" value={member.profile?.phone} />
          <Info label="Alamat" value={member.profile?.address} />
          <Info label="Daerah" value={member.profile?.daerah} />
          <Info label="Syarikat" value={member.profile?.company_name} />
          <Info label="Jawatan" value={member.profile?.position} />
          <Info label="Pekerjaan" value={member.profile?.occupation} />
          <Info label="Tahap Pendidikan" value={member.profile?.education_level} />
          <Info label="Kategori Keahlian" value={member.category_name} />
          <Info label="Ahli Sejak" value={member.member_since?.slice(0, 10)} />
          <Info label="Sah Sehingga" value={member.valid_until?.slice(0, 10)} />
        </div>
      )}

      {(member.profile?.course_name || member.profile?.course_date || member.profile?.course_session_year || member.profile?.course_venue) && (
        <div className="bg-white rounded-xl border border-piks-border p-6 mb-6 grid sm:grid-cols-2 gap-4 text-sm">
          <h2 className="sm:col-span-2 text-sm font-semibold text-piks-muted uppercase tracking-wide -mb-1">Maklumat Kursus</h2>
          <Info label="Nama Kursus" value={member.profile?.course_name} />
          <Info label="Tarikh Kursus" value={member.profile?.course_date} />
          <Info label="Sesi/Tahun" value={member.profile?.course_session_year} />
          <Info label="Tempat Kursus" value={member.profile?.course_venue} />
        </div>
      )}

      {(member.profile?.next_of_kin_name || member.profile?.next_of_kin_phone) && (
        <div className="bg-white rounded-xl border border-piks-border p-6 mb-6 grid sm:grid-cols-2 gap-4 text-sm">
          <h2 className="sm:col-span-2 text-sm font-semibold text-piks-muted uppercase tracking-wide -mb-1">Maklumat Waris</h2>
          <Info label="Nama Waris" value={member.profile?.next_of_kin_name} />
          <Info label="Hubungan" value={member.profile?.next_of_kin_relationship} />
          <Info label="No. Telefon Waris" value={member.profile?.next_of_kin_phone} />
        </div>
      )}

      {(member.profile?.ic_photo_file_id || member.profile?.ic_photo_back_file_id || member.profile?.passport_photo_file_id) && (
        <div className="bg-white rounded-xl border border-piks-border p-6 mb-6">
          <h2 className="text-sm font-semibold text-piks-muted uppercase tracking-wide mb-3">Dokumen Identiti</h2>
          <div className="flex flex-wrap gap-6">
            {member.profile?.ic_photo_file_id && (
              <div>
                <p className="text-piks-muted text-xs mb-2">Gambar Kad Pengenalan (Depan)</p>
                <ImagePreviewDialog
                  testId="ahli-ic-photo-link"
                  src={fileUrl(member.profile.ic_photo_file_id)}
                  alt="Gambar IC Depan"
                  className="w-36 h-24 rounded-lg object-cover border border-piks-border"
                />
              </div>
            )}
            {member.profile?.ic_photo_back_file_id && (
              <div>
                <p className="text-piks-muted text-xs mb-2">Gambar Kad Pengenalan (Belakang)</p>
                <ImagePreviewDialog
                  testId="ahli-ic-photo-back-link"
                  src={fileUrl(member.profile.ic_photo_back_file_id)}
                  alt="Gambar IC Belakang"
                  className="w-36 h-24 rounded-lg object-cover border border-piks-border"
                />
              </div>
            )}
            {member.profile?.passport_photo_file_id && (
              <div>
                <p className="text-piks-muted text-xs mb-2">Gambar Pasport</p>
                <ImagePreviewDialog
                  testId="ahli-passport-photo-link"
                  src={fileUrl(member.profile.passport_photo_file_id)}
                  alt="Gambar Pasport"
                  className="w-36 h-24 rounded-lg object-cover border border-piks-border"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {(member.profile?.documents || []).length > 0 && (
        <div className="bg-white rounded-xl border border-piks-border p-6 mb-6" data-testid="ahli-documents-section">
          <h2 className="text-sm font-semibold text-piks-muted uppercase tracking-wide mb-3">Dokumen Sokongan</h2>
          <div className="space-y-2">
            {member.profile.documents.map((d, i) => (
              <button
                key={i}
                type="button"
                onClick={() => openFileBlob(fileUrl(d.file_id))}
                className="flex items-center gap-2 text-sm text-piks-black hover:text-piks-red"
                data-testid={`ahli-document-${i}`}
              >
                <FileText className="w-4 h-4" /> {d.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {canManage && (
        <div className="bg-white rounded-xl border border-piks-border p-6 mb-6">
          <h2 className="text-sm font-semibold text-piks-muted uppercase tracking-wide mb-3">Kategori Keahlian</h2>
          <div className="flex flex-wrap gap-3">
            <Select value={newCategoryId} onValueChange={setNewCategoryId}>
              <SelectTrigger data-testid="category-change-select" className="w-56"><SelectValue /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={changeCategory} disabled={savingCategory || newCategoryId === member.membership_category_id} data-testid="category-change-submit-btn" className="bg-piks-black hover:bg-piks-black/90">
              {savingCategory ? "Menyimpan..." : "Kemaskini Kategori"}
            </Button>
          </div>
        </div>
      )}

      {canManage && (
        <div className="bg-white rounded-xl border border-piks-border p-6 mb-6">
          <h2 className="text-sm font-semibold text-piks-muted uppercase tracking-wide mb-3">Kemaskini Status Keahlian</h2>
          <div className="flex flex-wrap gap-3 mb-3">
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger data-testid="status-change-select" className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={changeStatus} disabled={saving || newStatus === member.status} data-testid="status-change-submit-btn" className="bg-piks-black hover:bg-piks-black/90">
              {saving ? "Menyimpan..." : "Kemaskini Status"}
            </Button>
          </div>
          <Textarea placeholder="Sebab perubahan status (pilihan)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="status-change-reason" />
        </div>
      )}

      {canManage && (
        <div className="bg-white rounded-xl border border-piks-border p-6 mb-6" data-testid="ahli-validity-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-piks-muted uppercase tracking-wide">Tempoh Keahlian</h2>
            <Button size="sm" variant="outline" onClick={renewOneYear} data-testid="ahli-renew-1yr-btn">
              Perbaharui +1 Tahun
            </Button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mb-3">
            <div>
              <Label>Sah Dari</Label>
              <Input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} className="mt-1.5" data-testid="ahli-valid-from-input" />
            </div>
            <div>
              <Label>Sah Sehingga</Label>
              <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className="mt-1.5" data-testid="ahli-valid-until-input" />
            </div>
          </div>
          <Textarea
            placeholder="Sebab perubahan tarikh (diperlukan)"
            value={validityReason}
            onChange={(e) => setValidityReason(e.target.value)}
            data-testid="ahli-validity-reason-input"
            className="mb-3"
          />
          <Button onClick={changeValidity} disabled={savingValidity} data-testid="ahli-validity-save-btn" className="bg-piks-black hover:bg-piks-black/90">
            {savingValidity ? "Menyimpan..." : "Kemaskini Tempoh Keahlian"}
          </Button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-piks-border p-6 mb-6">
        <h2 className="text-sm font-semibold text-piks-muted uppercase tracking-wide mb-3">Sejarah Status</h2>
        <div className="space-y-3">
          {(member.status_history || []).map((h, i) => (
            <div key={i} className="flex items-start gap-3" data-testid={`ahli-status-history-${i}`}>
              <Clock className="w-4 h-4 text-piks-muted mt-0.5" />
              <div>
                <p className="text-sm font-medium text-piks-black">{h.previous_status ? `${h.previous_status} → ${h.new_status}` : `Ditetapkan sebagai ${h.new_status}`}</p>
                <p className="text-xs text-piks-muted">{h.changed_at?.slice(0, 10)} {h.reason && `— ${h.reason}`}</p>
              </div>
            </div>
          ))}
          {(member.status_history || []).length === 0 && <p className="text-sm text-piks-muted">Tiada sejarah.</p>}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-piks-border p-6" data-testid="ahli-validity-history-section">
        <h2 className="text-sm font-semibold text-piks-muted uppercase tracking-wide mb-3">Sejarah Tempoh Keahlian</h2>
        <div className="space-y-3">
          {(member.validity_history || []).map((h, i) => (
            <div key={i} className="flex items-start gap-3" data-testid={`ahli-validity-history-${i}`}>
              <Clock className="w-4 h-4 text-piks-muted mt-0.5" />
              <div>
                <p className="text-sm font-medium text-piks-black">
                  Sah Sehingga: {h.previous_valid_until?.slice(0, 10) || "-"} → {h.new_valid_until?.slice(0, 10) || "-"}
                </p>
                <p className="text-xs text-piks-muted">{h.changed_at?.slice(0, 10)} — {h.reason}</p>
              </div>
            </div>
          ))}
          {(member.validity_history || []).length === 0 && <p className="text-sm text-piks-muted">Tiada sejarah.</p>}
        </div>
      </div>

      <Dialog open={!!resetResult} onOpenChange={(v) => !v && setResetResult(null)}>
        <DialogContent data-testid="ahli-reset-password-result-dialog">
          <DialogHeader>
            <DialogTitle>Kata Laluan Baharu Ditetapkan</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-piks-muted">
            Kata laluan baharu untuk <strong className="text-piks-black">{resetResult?.name}</strong> ({resetResult?.email}) hanya dipaparkan sekali. Sila salin dan berikan kepada ahli secara selamat.
          </p>
          <div className="flex items-center gap-2 bg-piks-ivory rounded-lg p-3 border border-piks-border">
            <code className="flex-1 font-mono text-sm text-piks-black break-all" data-testid="ahli-reset-password-value">{resetResult?.new_password}</code>
            <Button size="sm" variant="outline" onClick={copyPassword} data-testid="ahli-reset-password-copy-btn">
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-piks-muted text-xs">{label}</p>
      <p className="font-medium text-piks-black break-words">{value || "-"}</p>
    </div>
  );
}
